import { FunctionComponent, MouseEvent, useId, useRef, useState } from "react";
import { AriaMenuStatus, BusyButton, ButtonClass, focusFirstInteractiveElement, MenuAriaModel, Select, TextOption } from "@pjs/core-ui";
import { useFactoryRef, useOnMount } from "@pjs/react-utilities";
import { TemporaryAccessState } from "@pjs/user-domain";
import { userUiI18n } from "../i18n/UserUiI18n.const";
import { temporaryAccessActions } from "../actions/TemporaryAccessActions.const";
import { launchConfirmRevokeDialog } from "./functions/LaunchConfirmRevokeDialog.function";
import { TemporaryAccessInstructions } from "./TemporaryAccessInstructions.component";
import { getDaysDataSet } from "./functions/GetDaysDataSet";
import { ITemporaryAccessDetailsProps } from "./interfaces/ITemporaryAccessDetailsProps";
import { createDateFormatter } from "./utils/CreateDateFormatter.function";
import { createTimeFormatter } from "./utils/CreateTimeFormatter.function";
import "./styles/access-details.css";

const maxDaysToExtendBy = 7;

const getDateToDisplay = (stringDate: string, dateFormatter: Intl.DateTimeFormat, timeFormatter: Intl.DateTimeFormat): string => {
    const dateTime = new Date(stringDate);
    const date = dateFormatter.format(dateTime);
    const time = timeFormatter.format(dateTime);
    return userUiI18n.getString("temporary_access.details.date_time", { date: date, time: time });
};

export const TemporaryAccessDetails: FunctionComponent<ITemporaryAccessDetailsProps> = (props) => {
    const extendSelectId = useId();
    const revokeClicked = useRef<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [extendModel, setExtendModel] = useState<MenuAriaModel>({
        isOpen: false,
        activeItemIndex: 0,
        type: AriaMenuStatus.Closed
    });

    const langCode = userUiI18n.getLanguage();

    const dateFormatter = useFactoryRef(createDateFormatter, langCode);
    const timeFormatter = useFactoryRef(createTimeFormatter, langCode);
    const expiry = getDateToDisplay(props.model.details.expiry, dateFormatter, timeFormatter);
    const grantedOn = getDateToDisplay(props.model.details.creationDate, dateFormatter, timeFormatter);

    const extendAccessDataSet = useFactoryRef(getDaysDataSet, maxDaysToExtendBy);

    const onRevoke = (event: MouseEvent<HTMLElement>): void => {
        if (!revokeClicked.current) {
            revokeClicked.current = true;

            launchConfirmRevokeDialog(props.model.details.id, props.isTemporaryAccessSession, event.currentTarget, () => {
                revokeClicked.current = false;
            });
        }
    };

    useOnMount(() => {
        focusFirstInteractiveElement(containerRef.current);
    });

    return (
        <div ref={containerRef}>
            <TemporaryAccessInstructions supplementaryInformation={props.supplementaryInformation} />
            <div className="uui-temporary-access__step-container">
                <h3 className="uui-temporary-access__title">{userUiI18n.getString("temporary_access.details.title")}</h3>
                <p className="uui-temporary-access__description">{userUiI18n.getString("temporary_access.details.description")}</p>
                <table className="uui-access-details__table">
                    <tbody>
                        <tr>
                            <td className="uui-access-details__table-cell">{userUiI18n.getString("temporary_access.details.username")}</td>
                            <td className="uui-access-details__table-cell">{props.model.details.grantedTo}</td>
                        </tr>
                        <tr>
                            <td className="uui-access-details__table-cell">{userUiI18n.getString("temporary_access.details.granted_on")}</td>
                            <td className="uui-access-details__table-cell" data-hook="granted-on-date">
                                {grantedOn}
                            </td>
                        </tr>
                        <tr>
                            <td className="uui-access-details__table-cell">{userUiI18n.getString("temporary_access.details.expires_on")}</td>
                            <td className="uui-access-details__table-cell" data-hook="expiry-date">
                                {expiry}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <BusyButton
                    isBusy={props.model.state.descriptor === TemporaryAccessState.RevokePending}
                    ariaLabel={userUiI18n.getString("temporary_access.revoke_access.a11y.label")}
                    className={ButtonClass.Outlined}
                    dataHook="revoke-access-btn"
                    onClick={onRevoke}>
                    {userUiI18n.getString("temporary_access.revoke_access.revoke_button_text")}
                </BusyButton>
            </div>

            {props.model.details.hasBeenAccessed && !props.isTemporaryAccessSession && (
                <div className="uui-temporary-access__extension-container">
                    <h3 id={extendSelectId} className="uui-temporary-access__extension-heading">
                        {userUiI18n.getString("temporary_access.extend_access.title")}
                    </h3>
                    <p className="uui-temporary-access__extension-description">{userUiI18n.getString("temporary_access.extend_access.description")}</p>
                    <div className="uui-temporary-access__extension-select">
                        <Select
                            model={extendModel}
                            onModelChange={(model) => setExtendModel(model)}
                            dataSet={extendAccessDataSet}
                            labelId={extendSelectId}
                            renderOption={TextOption}
                        />
                    </div>
                    <BusyButton
                        isBusy={props.model.state.descriptor === TemporaryAccessState.ExtendPending}
                        ariaLabel={userUiI18n.getString("temporary_access.extend_access.a11y.label")}
                        className={ButtonClass.Outlined}
                        dataHook="extend-access-btn"
                        onClick={() => temporaryAccessActions.extendTemporaryAccess(props.model.details.id, extendModel.activeItemIndex + 1)}>
                        {userUiI18n.getString("temporary_access.extend_access.button")}
                    </BusyButton>
                </div>
            )}
        </div>
    );
};
